import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, Wifi } from 'lucide-react';
import { useTour } from '@/contexts/TourContext';

export function ConnectionStatus({ status, error, modelId }) {
  const { currentTour } = useTour();
  
  const getStatusContent = () => {
    switch (status) {
      case 'idle':
        return {
          title: 'Loading Tour',
          description: 'Preparing your 3D experience...',
          showSpinner: true,
        };
      case 'connecting':
        return {
          title: 'Loading Tour',
          description: 'Connecting to 3D environment...',
          showSpinner: true,
        };
      case 'connected':
        return {
          title: 'Ready',
          description: 'Tour loaded successfully',
          showSpinner: false,
        };
      case 'error':
        return {
          title: 'Unable to Load Tour',
          description: error || 'There was a problem loading this tour',
          showSpinner: false,
          isError: true,
        };
      default:
        return {
          title: 'Loading',
          description: 'Please wait...',
          showSpinner: true,
        };
    }
  };

  const content = getStatusContent();

  // For connected state, don't show anything - let the viewer show
  if (status === 'connected') {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center max-w-md">
      
      {content.showSpinner && (
        <div className="relative mb-6">
          <div className="w-12 h-12 border-4 border-muted rounded-full" />
          <div className="absolute inset-0 w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {content.isError && (
        <div className="mb-4 p-3 rounded-full bg-destructive/10">
          <XCircle className="h-8 w-8 text-destructive" />
        </div>
      )}
      
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {content.title}
      </h3>
      
      {currentTour?.name && !content.isError && (
        <p className="text-sm text-primary mb-2">{currentTour.name}</p>
      )}
      
      <p className="text-sm text-muted-foreground mb-4">
        {content.description}
      </p>

      {content.isError && (
        <div className="mt-4 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-left">
          <p className="text-xs text-destructive font-medium mb-2">What you can try:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Refresh the page</li>
            <li>• Check your internet connection</li>
            <li>• Contact your administrator if the problem persists</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default ConnectionStatus;
